import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { downloadFile } from '../utils/downloadFile';
import { Upload, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCT_TYPES = [
  'Term', 'Endowment', 'ULIP', 'Whole Life', 'Health',
  'Motor', 'Travel', 'Home', 'Personal Accident', 'Commercial',
];

// Standard annual policy term = start + 364 days (365-day inclusive window)
const DEFAULT_TERM_DAYS = 364;

const addDaysISO = (isoDate, days) => {
  if (!isoDate) return '';
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return '';
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const emptyForm = (clientId = '', productType = '') => {
  const pt = productType || '';
  const defaultGst = pt === '' ? '' : ((pt === 'Term' || pt === 'Term Life' || pt === 'Health') ? '0' : '18');
  return {
    client_id: clientId,
    policy_number: '',
    insurer: '',
    product_name: '',
    product_type: pt,
    plan_type: '',
    sum_assured: '',
    premium_amount: '',
    net_premium: '',
    gst_percent: defaultGst,
    gst_amount: '',
    total_premium: '',
    premium_frequency: 'Yearly',
    start_date: '',
    end_date: '',
    next_due_date: '',
    nominee: '',
    status: 'Active',
    policy_stage: '',  // Round 61 · Item 4 — blank = "Not set"
  };
};

/**
 * Shared Add-Policy dialog. Used by:
 *  - Global header "+ Add Policy" button
 *  - Clients page per-row action
 *  - Client detail (Manage) page action
 *  - Dashboard Quick Actions (Health/Life/Motor)
 *  - Inline follow-up after Add Client
 *
 * Loads its own client list (so it works from any page).
 */
const AddPolicyDialog = ({ open, onClose, initialClientId = '', initialProductType = '', onCreated }) => {
  const [clients, setClients] = useState([]);
  const [formData, setFormData] = useState(() => emptyForm(initialClientId, initialProductType));
  const [saving, setSaving] = useState(false);
  const [policyDocFile, setPolicyDocFile] = useState(null);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [showBulk, setShowBulk] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFormData(emptyForm(initialClientId, initialProductType));
    setBulkFile(null); setBulkResult(null); setShowBulk(false);
    axios.get(`${API}/clients`, { withCredentials: true })
      .then((r) => setClients(r.data))
      .catch((err) => console.warn('Client list load failed:', err?.message));
  }, [open, initialClientId, initialProductType]);

  const downloadTemplate = async () => {
    const res = await downloadFile('/templates/policy.xlsx', 'policy_template.xlsx');
    if (!res.ok) {
      console.error('Policy template download failed:', res.error);
      toast.error(res.error?.toLowerCase().includes('auth') || res.error?.toLowerCase().includes('token')
        ? 'Your session expired — please sign in again and retry.'
        : `Could not download template: ${res.error}`);
    } else {
      toast.success('Policy template downloaded');
    }
  };

  const uploadBulk = async () => {
    if (!bulkFile) { toast.error('Choose an Excel file first'); return; }
    setBulkUploading(true); setBulkResult(null);
    try {
      const fd = new FormData();
      fd.append('file', bulkFile);
      const { data } = await axios.post(`${API}/policies/bulk-upload`, fd, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
      });
      setBulkResult(data);
      toast.success(`Imported ${data.inserted} policy record(s)`);
      // Round 46 · Item 2: 30-second Undo affordance for policy bulk imports.
      // Mirrors the Clients page pattern — only offered when at least one row
      // landed and we got a batch_id back.
      if (data.batch_id && data.inserted > 0) {
        toast('Import committed', {
          description: `${data.inserted} policy record(s) added. You can undo this batch for the next 30 seconds.`,
          duration: 30000,
          action: {
            label: 'Undo import',
            onClick: async () => {
              try {
                const { data: undo } = await axios.post(
                  `${API}/policy-imports/${data.batch_id}/undo`,
                  {}, { withCredentials: true },
                );
                const n = (undo.deleted_policies || 0) + (undo.deleted_products || 0);
                toast.success(`Rolled back ${n} record(s)`);
                if (onCreated) onCreated({ bulkInserted: -n });
              } catch { toast.error('Undo failed'); }
            },
          },
        });
      }
      if (onCreated) onCreated({ bulkInserted: data.inserted });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk upload failed');
    } finally { setBulkUploading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.client_id) { toast.error('Please select a client'); return; }
    const sum = parseFloat(formData.sum_assured);
    // Premium Amount field was removed from every form in Round 28 (form only
    // captures Net Premium + GST + auto-computed Total). Derive the effective
    // premium from what the user actually filled — Net Premium is the primary
    // signal, with Total as a fallback. Backend also has this same fallback
    // (routers/policies.py lines 115-118) but we still validate here so the
    // error surfaces before we round-trip to the server.
    const net = parseFloat(formData.net_premium);
    const total = parseFloat(formData.total_premium);
    const prem = Number.isFinite(net) && net > 0 ? net : (Number.isFinite(total) && total > 0 ? total : NaN);
    if (!Number.isFinite(sum) || sum <= 0) { toast.error('Enter a valid Sum Assured'); return; }
    if (!Number.isFinite(prem) || prem <= 0) { toast.error('Enter a valid Net Premium'); return; }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        sum_assured: sum,
        premium_amount: prem,
        net_premium: formData.net_premium === '' ? null : parseFloat(formData.net_premium),
        gst_percent: formData.gst_percent === '' ? null : parseFloat(formData.gst_percent),
        gst_amount: formData.gst_amount === '' ? null : parseFloat(formData.gst_amount),
        total_premium: formData.total_premium === '' ? null : parseFloat(formData.total_premium),
      };
      const { data } = await axios.post(`${API}/policies`, payload, { withCredentials: true });
      toast.success('Policy created');
      // Best-effort policy document upload after the row exists.
      if (policyDocFile && data.policy_id) {
        try {
          const fd = new FormData();
          fd.append('file', policyDocFile);
          await axios.post(`${API}/policies/${data.policy_id}/documents?doc_type=policy`, fd, {
            withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
          });
          toast.success(`Policy document "${policyDocFile.name}" attached`);
        } catch (docErr) {
          toast.warning(docErr.response?.data?.detail || 'Policy created, but document upload failed. Try again from Edit Policy.');
        }
      }
      // Optimistically log a commission using the configured rate for this product type.
      try {
        const { data: comm } = await axios.get(`${API}/commission-rates/preview`, {
          withCredentials: true,
          params: { product_type: formData.product_type, premium: prem, insurer: formData.insurer, policy_stage: 'New Business' },
        });
        if (comm?.matched && comm.commission_amount > 0) {
          await axios.post(`${API}/commissions`, {
            policy_id: data.policy_id,
            commission_type: 'first_year',
            amount: comm.commission_amount,
            date: new Date().toISOString().slice(0, 10),
            rate_percent: comm.rate_percent,
            premium_basis: prem,
            auto_calculated: true,
          }, { withCredentials: true });
          const tierNote = comm.insurer
            ? `${comm.insurer} · ${comm.product_type} · ${comm.policy_stage}`
            : `${comm.product_type} default`;
          toast.success(`Commission auto-logged: ₹${comm.commission_amount.toFixed(2)} (${comm.rate_percent}% — ${tierNote})`);
        }
      } catch (e) { console.warn('Commission auto-log skipped:', e?.message); }
      if (onCreated) onCreated({ ...formData, ...data });
      onClose();
    } catch (err) {
      console.error('Create policy failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to create policy');
    } finally {
      setSaving(false);
    }
  };

  const update = (patch) => setFormData((f) => ({ ...f, ...patch }));

  // Round 59 · Hardened next_due_date auto-sync. Flips to true the moment
  // the agent touches the due-date field by hand; from that moment the
  // auto-fill in onStartDateChange / onEndDateChange becomes a no-op.
  // Reset back to false on dialog close (handled by the parent via `open`).
  const [dueManuallySet, setDueManuallySet] = useState(false);

  // Auto-fill End Date as (Start Date + 364 days) and Next Due Date as
  // matching the End Date. Both remain editable — only overwrite when the
  // value is empty or still equals the previous auto value, so any manual
  // customisation stays intact. The `dueManuallySet` guard freezes the due
  // date once the agent has typed into it by hand (Round 59 Item 2).
  const onStartDateChange = (nextStart) => {
    setFormData((f) => {
      const prevAuto = f.start_date ? addDaysISO(f.start_date, DEFAULT_TERM_DAYS) : '';
      const shouldAutoFillEnd = !f.end_date || f.end_date === prevAuto;
      const nextEnd = shouldAutoFillEnd && nextStart ? addDaysISO(nextStart, DEFAULT_TERM_DAYS) : f.end_date;
      const shouldAutoFillDue = !dueManuallySet && (!f.next_due_date || f.next_due_date === prevAuto || f.next_due_date === f.end_date);
      const nextDue = shouldAutoFillDue ? nextEnd : f.next_due_date;
      return { ...f, start_date: nextStart, end_date: nextEnd, next_due_date: nextDue };
    });
  };

  const onEndDateChange = (nextEnd) => {
    setFormData((f) => {
      // If the agent hasn't manually edited the due date, keep it in sync
      // with the new end date — covers both Add (where next_due_date starts
      // empty) and Edit (where it starts matching the old end_date).
      const shouldSync = !dueManuallySet;
      return { ...f, end_date: nextEnd, next_due_date: shouldSync ? nextEnd : f.next_due_date };
    });
  };

  // Called when the agent types into the Next Due Date input. Once touched,
  // the guard latches and the end-date → due-date sync stops firing.
  const onDueDateChange = (nextDue) => {
    setDueManuallySet(true);
    setFormData((f) => ({ ...f, next_due_date: nextDue }));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="add-policy-dialog">
        <DialogHeader>
          <DialogTitle>Add Product{initialProductType ? ` — ${initialProductType}` : ''}</DialogTitle>
          <DialogDescription>Enter the policy details. Client is pre-filled if you opened this from a client row.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between gap-2 p-2 rounded-lg border border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <Upload className="h-4 w-4 text-blue-700" />
            <span>Have many policies? Upload from Excel.</span>
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" size="sm" variant="ghost" onClick={downloadTemplate} data-testid="policy-download-template">
              <Download className="h-3.5 w-3.5 mr-1" /> Download Template
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => setShowBulk(v => !v)} data-testid="policy-bulk-toggle">
              {showBulk ? 'Hide bulk upload' : 'Bulk upload'}
            </Button>
          </div>
        </div>

        {showBulk && (
          <div className="p-3 rounded-lg border border-blue-200 bg-blue-50/40 space-y-2" data-testid="policy-bulk-panel">
            <Input type="file" accept=".xlsx,.xls" onChange={(e) => setBulkFile(e.target.files?.[0] || null)} data-testid="policy-bulk-file-input" />
            <div className="flex justify-end">
              <Button type="button" size="sm" onClick={uploadBulk} disabled={bulkUploading || !bulkFile} className="bg-blue-800 hover:bg-blue-900" data-testid="policy-bulk-submit">
                {bulkUploading ? 'Uploading…' : 'Import Policies'}
              </Button>
            </div>
            {bulkResult && (
              <div className="text-xs">
                <p><strong>Inserted:</strong> {bulkResult.inserted} / {bulkResult.total_rows}</p>
                {bulkResult.errors?.length > 0 && (
                  <ul className="mt-1 text-red-700 max-h-24 overflow-y-auto">
                    {bulkResult.errors.slice(0, 10).map((e) => <li key={e}>• {e}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-2">
              <Label>Client *</Label>
              <Select value={formData.client_id} onValueChange={(v) => update({ client_id: v })}>
                <SelectTrigger data-testid="policy-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Policy Number *</Label>
              <Input value={formData.policy_number} onChange={(e) => update({ policy_number: e.target.value })} required data-testid="policy-number-input" />
            </div>
            <div className="space-y-2">
              <Label>Insurer *</Label>
              <Input value={formData.insurer} onChange={(e) => update({ insurer: e.target.value })} required data-testid="policy-insurer-input" />
            </div>
            <div className="space-y-2">
              <Label>Product Type *</Label>
              <Select value={formData.product_type} onValueChange={(v) => {
                const isZeroGst = v === 'Term' || v === 'Term Life' || v === 'Health';
                update({ product_type: v, gst_percent: isZeroGst ? '0' : (formData.gst_percent || '18') });
              }}>
                <SelectTrigger data-testid="policy-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRODUCT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Product Name *</Label>
              <Input value={formData.product_name} onChange={(e) => update({ product_name: e.target.value })} required
                disabled={!formData.product_type}
                placeholder={formData.product_type ? '' : 'Select Product Type first'}
                data-testid="policy-product-input" />
            </div>
            <div className="space-y-2">
              <Label>Sum Assured (₹) *</Label>
              <Input type="number" min="0" placeholder="Enter sum assured" value={formData.sum_assured} onChange={(e) => update({ sum_assured: e.target.value })} required data-testid="policy-sum-input" />
            </div>
            <div className="space-y-2">
              <Label>Net Premium (₹) *</Label>
              <Input type="number" min="0" placeholder="Base premium before GST" value={formData.net_premium}
                required
                onChange={(e) => {
                  const net = e.target.value;
                  setFormData((f) => {
                    const netN = parseFloat(net) || 0;
                    const gstPct = parseFloat(f.gst_percent) || 0;
                    const gstAmt = netN * (gstPct / 100);
                    const nextTotal = f.total_premium_touched ? f.total_premium : (net === '' && !gstPct ? '' : (netN + gstAmt).toFixed(2));
                    return { ...f, net_premium: net, gst_amount: gstAmt ? gstAmt.toFixed(2) : '', total_premium: nextTotal };
                  });
                }}
                data-testid="policy-net-premium-input" />
            </div>
            <div className="space-y-2">
              <Label>GST % *</Label>
              <Input type="number" min="0" step="0.1" placeholder="18 (or 0 for Term/Health)"
                value={formData.gst_percent}
                onChange={(e) => {
                  const gstPct = e.target.value;
                  setFormData((f) => {
                    const net = parseFloat(f.net_premium) || 0;
                    const pct = parseFloat(gstPct) || 0;
                    const gstAmt = net * pct / 100;
                    const nextTotal = f.total_premium_touched ? f.total_premium : (net === 0 ? '' : (net + gstAmt).toFixed(2));
                    return { ...f, gst_percent: gstPct, gst_amount: gstAmt ? gstAmt.toFixed(2) : '', total_premium: nextTotal };
                  });
                }}
                data-testid="policy-gst-input" />
              <p className="text-[10px] text-slate-500">Defaults to 18%. Term Life & Health are typically 0%.</p>
            </div>
            <div className="space-y-2">
              <Label>Total Premium (₹)</Label>
              <Input type="number" min="0" placeholder="Auto: Net + GST (editable)" value={formData.total_premium}
                onChange={(e) => setFormData((f) => ({ ...f, total_premium: e.target.value, total_premium_touched: true }))}
                data-testid="policy-total-premium-input" />
              <p className="text-[10px] text-slate-500">Auto-computed as Net + GST. Once you edit this, auto-calc stops until you clear the field.</p>
            </div>
            <div className="space-y-2">
              <Label>Frequency *</Label>
              <Select value={formData.premium_frequency} onValueChange={(v) => update({ premium_frequency: v })}>
                <SelectTrigger data-testid="policy-frequency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Monthly">Monthly</SelectItem>
                  <SelectItem value="Quarterly">Quarterly</SelectItem>
                  <SelectItem value="Half-Yearly">Half-Yearly</SelectItem>
                  <SelectItem value="Yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Start Date *</Label>
              <Input type="date" value={formData.start_date} onChange={(e) => onStartDateChange(e.target.value)} required data-testid="policy-start-input" />
            </div>
            <div className="space-y-2">
              <Label>End Date *</Label>
              <Input type="date" value={formData.end_date} onChange={(e) => onEndDateChange(e.target.value)} required data-testid="policy-end-input" />
              <p className="text-[10px] text-slate-500">Defaults to Start Date + 364 days for annual policies. Edit freely for multi-year or short-term.</p>
            </div>
            <div className="space-y-2">
              <Label>Next Due Date *</Label>
              <Input type="date" value={formData.next_due_date} onChange={(e) => onDueDateChange(e.target.value)} required data-testid="policy-due-input" />
              <p className="text-[10px] text-slate-500">Defaults to End Date. For monthly/quarterly modes, set the next premium due date manually.</p>
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Nominee</Label>
              <Input value={formData.nominee} onChange={(e) => update({ nominee: e.target.value })} data-testid="policy-nominee-input" />
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Policy Stage</Label>
              <Select value={formData.policy_stage || 'not_set'} onValueChange={(v) => update({ policy_stage: v === 'not_set' ? '' : v })}>
                <SelectTrigger data-testid="policy-stage-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="not_set">Not set</SelectItem>
                  <SelectItem value="New Business">New</SelectItem>
                  <SelectItem value="Renewal">Renewal</SelectItem>
                  <SelectItem value="Both">Both</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-500">Used by the commission tracker to pick the correct rate.</p>
            </div>
            <div className="col-span-2 rounded-md border border-blue-100 bg-blue-50/40 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-800">Policy Document</p>
                  <p className="text-[11px] text-slate-500">Optional. Attach the PDF/scan of this policy (max 10MB). Upload runs after save.</p>
                </div>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp"
                  onChange={(e) => setPolicyDocFile(e.target.files?.[0] || null)}
                  className="text-xs" data-testid="policy-doc-file-input" />
              </div>
              {policyDocFile && (
                <p className="text-xs text-blue-800">Selected: <strong>{policyDocFile.name}</strong> ({Math.round(policyDocFile.size / 1024)} KB)</p>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="bg-blue-800 hover:bg-blue-900" disabled={saving} data-testid="policy-submit-button">
              {saving ? 'Creating…' : 'Create Policy'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddPolicyDialog;
